import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useThrottledCallback } from 'use-debounce';

import BackButton from './BackButton';
import DraftInfo from './DraftInfo';
import Notification from './Notification';
import { loadingDarkIcon } from '../../assets';
import { useEditPageTrack } from '../../hooks/useEditPageTrack';
import { useSaveDraft } from '../../hooks/useSaveDraft';
import {
  MirrorExperiment,
  setToastInfo,
  setToastVisible,
  submitWorkspaceDraft,
  updateSettingInfo,
  useAppDispatch,
  useAppSelector,
} from '../../state';
import { track } from '../../track';
import { ChatModelType, EGender } from '../../types';
import RichTextArea from '../Edit/RichTextArea';
import { ENpcStatusTrackText } from '../Home';
import { getNpcStatus } from '../Home/NpcList';
import { LocalImage } from '../Image';
import { NotifyModal } from '../NotifyModal';
import { message } from '../message';

interface IEditHeaderProps {
  onPublish?: () => void;
  styles: Record<string, string>;
}

export const PAGE = 'create_npc_advanced_page';

// dialog_model_choice埋点 对话模型：dialog、工具模型:tool、若无则为空
export const getDialogModelChoice = (chat_model_type: ChatModelType | any) => {
  return chat_model_type === ChatModelType.Chat
    ? 'dialog'
    : chat_model_type === ChatModelType.Util
      ? 'tool'
      : '';
};

export function EditHeader(props: IEditHeaderProps) {
  const { styles } = props;

  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const {
    npcId,
    statusInfo,
    settingInfo,
    imgInfo,
    submitLoading: loading,
    isToastVisible,
    toastStatus,
    voiceInfo,
  } = useAppSelector((state) => state.edit);
  const { chat_model_type, desc, long_desc } = settingInfo;
  const status = getNpcStatus(statusInfo);
  const { trackParams } = useEditPageTrack();

  const { saveDraft, saving, saveTime } = useSaveDraft();

  const handlePublish = useThrottledCallback(
    async () => {
      track.send('publish_npc_btn_click', PAGE, {
        npc_id: npcId,
        ugc_npc_approving_status: ENpcStatusTrackText[status],
        dialog_model_choice: chat_model_type,
        plugin_list: trackParams.plugin_list,
      });

      // Check for required fields
      if (!settingInfo.npc_name) {
        return message.error(t('nickname_required_for_publish'));
      }

      if (settingInfo.gender === EGender.NotDefined) {
        return message.error(t('gender_required_for_publish'));
      }

      if (!settingInfo.desc) {
        return message.error(t('desc_required_for_publish'));
      }

      if (!settingInfo.long_desc) {
        return message.error(t('long_desc_required_for_publish'));
      }

      if (!settingInfo.prologue) {
        return message.error(t('prologue_required_for_publish'));
      }

      if (!voiceInfo.npc_tone) {
        return message.error(t('voice_required_for_publish'));
      }

      if (!imgInfo.base_img_url || !imgInfo.face_img_url) {
        return message.error(t('img_required_for_publish'));
      }

      await saveDraft();

      // If all checks pass, proceed with publishing
      dispatch(submitWorkspaceDraft());
    },
    1000,
    { leading: true, trailing: false }
  );

  const onClose = useCallback(() => {
    dispatch(setToastVisible(false));
    dispatch(setToastInfo(undefined));
    if (toastStatus === 'success') {
      location.href = '/create';
    }
  }, [toastStatus]);

  const [notifyModalVisible, setNotifyModalVisible] = useState(false);
  const { desc_has_upgrade } = useMemo(() => statusInfo, []);
  const autoContactLongDesc = useCallback(() => {
    dispatch(
      updateSettingInfo({
        long_desc: `${desc}\n${long_desc}`,
      })
    );
    //更新富文本组件 内部状态
    RichTextArea.getRef('edit_setting_info_long_desc')?.reset(`${desc}\n${long_desc}`);
  }, [desc, dispatch, long_desc]);

  useEffect(() => {
    if (!!npcId && !desc_has_upgrade) {
      setNotifyModalVisible(true);
    }
  }, []);

  return (
    <header className={styles.header}>
      <section className={styles.headerSection}>
        <BackButton styles={styles} />
        <DraftInfo styles={styles} userSaveTime={saveTime} />
      </section>

      <div className="flex">
        <button className={styles.saveButton} onClick={saveDraft}>
          {saving && <LocalImage src={loadingDarkIcon} />}
          {t('save_draft')}
        </button>

        <button className={styles.publishButton} onClick={handlePublish}>
          {loading && <LocalImage src={loadingDarkIcon} />}
          {t('publish')}
        </button>
      </div>

      {/* 发布状态Toast */}
      <Notification type={toastStatus} styles={styles} onClose={onClose} open={isToastVisible} />
      <NotifyModal
        visible={notifyModalVisible}
        onClose={() => {
          setNotifyModalVisible(false);
          autoContactLongDesc();
        }}
        title={t('notify_modal_title')}
        descriptions={[
          t('chat_upgrade_modal_description'),
          t('chat_upgrade_modal_description_sub'),
        ]}
        okText={t('notify_modal_ok_text')}
      />
    </header>
  );
}

export default EditHeader;
